import { Box, Typography } from '@mui/material';
import { Container, Heading } from './index';

/**
 * @typedef {Object} SectionHeaderProps
 * @property {string} title - Section title
 * @property {string} [subtitle] - Optional subtitle/description (displayed below green bar in light mode, inside dark section in dark mode)
 * @property {React.ReactNode} [badge] - Optional badge element (e.g., "OPENING SOON")
 * @property {string} [badgeText] - Optional badge text
 * @property {React.ReactNode} [image] - Optional image/logo element above title
 * @property {string} [imageSrc] - Optional image source URL
 * @property {string} [imageAlt] - Optional image alt text
 * @property {number} [imageHeight] - Optional image height
 * @property {boolean} [dark] - Dark theme variant (black background, white text, subtitle inside)
 * @property {Object} [sx] - Additional MUI sx props
 */

/**
 * Reusable Section Header component with green bar treatment
 *
 * Features:
 * - Full-width green bar borders (top and bottom)
 * - Optional image/logo above title
 * - Optional badge beside title
 * - Optional subtitle (positioned below green bar in light mode, inside dark section in dark mode)
 * - Dark theme support (black background, white text)
 * - Always center-aligned
 *
 * Dark Mode Behavior:
 * - Black background (#000000)
 * - White title text
 * - Light gray subtitle text (rgba(255, 255, 255, 0.8))
 * - Subtitle positioned INSIDE the dark section (not below)
 *
 * Light Mode Behavior:
 * - Light green background (color-mix(in srgb, var(--color-primary) 5%, transparent))
 * - Dark title text
 * - Subtitle positioned BELOW the green bar section
 *
 * @param {SectionHeaderProps} props - Component props
 * @returns {JSX.Element} Section header component
 */
function SectionHeader({
  title,
  subtitle,
  badge,
  badgeText,
  image,
  imageSrc,
  imageAlt = '',
  imageHeight = 40,
  dark = false,
  sx = {},
  ...props
}) {
  const titleColor = dark ? 'var(--color-secondary-text)' : 'text.primary';
  const subtitleColor = dark ? 'rgba(255, 255, 255, 0.8)' : 'text.primary';
  const backgroundColor = dark
    ? 'var(--color-secondary)'
    : 'color-mix(in srgb, var(--color-primary) 5%, transparent)';

  // Create badge element if badgeText is provided
  const badgeElement =
    badge ||
    (badgeText && (
      <Typography
        variant="overline"
        sx={{
          color: 'primary.main',
          fontWeight: 600,
          fontSize: '0.875rem',
          letterSpacing: '0.15em',
          backgroundColor:
            'color-mix(in srgb, var(--color-primary) 20%, transparent)',
          px: 2,
          py: 0.5,
          borderRadius: 1,
          whiteSpace: 'nowrap',
        }}
      >
        {badgeText}
      </Typography>
    ));

  // Create image element if imageSrc is provided
  const imageElement =
    image ||
    (imageSrc && (
      <Box
        component="img"
        src={imageSrc}
        alt={imageAlt}
        sx={{
          height: imageHeight,
          width: 'auto',
          mb: 2,
          mt: -1,
          '@media (max-width: 768px)': {
            height: imageHeight * 0.8,
          },
        }}
      />
    ));

  return (
    <>
      {/* Green Bar Header Treatment - Full Width */}
      <Box
        sx={{
          borderTop: '4px solid var(--color-primary)',
          borderBottom: '4px solid var(--color-primary)',
          backgroundColor,
          py: 4,
          mb: dark ? 12 : subtitle ? 6 : 12, // Always 12px margin in dark mode, 6px if subtitle (light mode)
          width: '100vw',
          position: 'relative',
          left: '50%',
          right: '50%',
          marginLeft: '-50vw',
          marginRight: '-50vw',
          ...sx,
        }}
        {...props}
      >
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center' }}>
            {/* Optional Image */}
            {imageElement}

            {/* Title with optional badge */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 3,
                flexWrap: 'wrap',
              }}
            >
              <Heading
                variant="h2"
                sx={{
                  color: titleColor,
                  fontWeight: 600,
                  fontSize: { xs: '2.5rem', md: '3rem' },
                  fontFamily: '"Playfair Display", serif',
                  margin: 0,
                  mb: 0,
                }}
              >
                {title}
              </Heading>

              {/* Optional Badge */}
              {badgeElement}
            </Box>

            {/* Subtitle inside dark section when dark mode is enabled */}
            {dark && subtitle && (
              <Typography
                sx={{
                  color: subtitleColor,
                  fontSize: '1.125rem',
                  lineHeight: 1.6,
                  textAlign: 'center',
                  mt: 0.5,
                  maxWidth: '800px',
                  mx: 'auto',
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Container>
      </Box>

      {/* Optional subtitle below green bar (light mode only) */}
      {!dark && subtitle && (
        <Container maxWidth="xl">
          <Typography
            sx={{
              color: subtitleColor,
              fontSize: '1.125rem',
              lineHeight: 1.6,
              textAlign: 'center',
              mb: 4,
            }}
          >
            {subtitle}
          </Typography>
        </Container>
      )}
    </>
  );
}

export default SectionHeader;
